import { useRef, useEffect, useCallback, useState } from "react";
import SignaturePad from "signature_pad";
import { Button } from "./ui/button";
import { dataURLtoFile } from "./utils";
import { API_BASE_URL } from "../environments/environment";
import { toast } from "react-toastify";
// New dedicated component for the Signature Pad field
interface SignatureFieldInternalProps {
    field: any; // The field definition object
    value: File | string | null;
    onChange: (fieldId: string, value: File | string | null) => void;
    dataURLtoFile: (dataurl: string, filename: string) => File | null;
    errorClass: string;
    baseClasses: string; // If needed for styling consistency
    // No need to pass API_BASE_URL if it's globally available or handled by image URLs
}


export const SignatureFieldInternal: React.FC<SignatureFieldInternalProps> = ({
    field,
    value,
    onChange,
    dataURLtoFile,
    errorClass,
    // baseClasses
}) => {
    const signaturePadInstanceRef = useRef<SignaturePad | null>(null);
    const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isSigned, setIsSigned] = useState(false);

    // Use useCallback to memoize the resize logic
    // This function is responsible for setting the canvas dimensions
    const resizeCanvas = useCallback(() => {
        const canvas = signatureCanvasRef.current;
        const container = containerRef.current;
        const sigPad = signaturePadInstanceRef.current;

        if (canvas && container) {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            // Get container width and subtract padding (assuming p-4 is 16px total padding)
            // Adjust the calculation slightly to account for border and potential scrollbar space if necessary
            const containerWidth = container.offsetWidth;
            const canvasDisplayWidth = Math.min(containerWidth - 32, 500); // Subtracting 16px for each side (p-4 is 1rem = 16px, so 32px total)
            const canvasDisplayHeight = Math.round(canvasDisplayWidth * 0.4); // Maintain 2.5:1 aspect ratio

            // Set the canvas element's size for drawing and display
            canvas.width = canvasDisplayWidth * ratio;
            canvas.height = canvasDisplayHeight * ratio;
            canvas.style.width = `${canvasDisplayWidth}px`;
            canvas.style.height = `${canvasDisplayHeight}px`;

            // Restore context settings like scale and line width after resizing
            const ctx = canvas.getContext("2d");
            if (ctx && sigPad) {
                ctx.scale(ratio, ratio);
                // Reapply SignaturePad's internal pen size settings if needed, or rely on its internal resize handling
                // SignaturePad is generally good at handling this if canvas attributes are set correctly.
                // Reinitializing the pad with existing data is safer for complex redraws.
            }

            // Attempt to redraw the current signature after resize to prevent distortion
            // Only redraw if the pad instance exists, has data, AND we are in drawing mode (value is null)
            if (sigPad && !sigPad.isEmpty() && !value) {
                const data = sigPad.toData(); // Save current signature data
                sigPad.clear(); // Clear the canvas
                // Use setTimeout to allow canvas to fully resize before redrawing
                setTimeout(() => {
                    // Ensure the pad instance is still valid before redrawing
                    if (signaturePadInstanceRef.current) {
                        signaturePadInstanceRef.current.fromData(data); // Redraw with new dimensions
                    }
                }, 0);
            }
        }
    }, []); // No dependencies, this is a static utility function now

    // Effect for initial setup and value changes
    useEffect(() => {
        const canvas = signatureCanvasRef.current;
        if (canvas) {
            const signaturePad = new SignaturePad(canvas, {
                penColor: "black",
                velocityFilterWeight: 0.7,
                minWidth: 0.5,
                maxWidth: 2.5,
            });
            signaturePadInstanceRef.current = signaturePad;

            resizeCanvas();
            window.addEventListener("resize", resizeCanvas);

            // If there's an initial value (a filename from the server), load it.
            if (typeof value === 'string' && value) {
                const imageUrl = `${API_BASE_URL}/media/get-form_entry-file/${value}`;
                signaturePad.fromDataURL(imageUrl, { ratio: 1 });
                signaturePad.off(); // Disable drawing
                setIsSigned(true);
            }

            return () => {
                window.removeEventListener("resize", resizeCanvas);
                signaturePad.off();
            };
        }
    }, []); // Only run once on mount

    const handleSaveSignature = () => {
        const signaturePad = signaturePadInstanceRef.current;
        if (signaturePad) {
            if (signaturePad.isEmpty()) {
                toast.error("Please provide a signature first.");
                return;
            }
            const sigDataUrl = signaturePad.toDataURL("image/png");
            const file = dataURLtoFile(sigDataUrl, `${field.id}_signature.png`);
            if (file) {
                onChange(field.id, file); // Send file to parent
                signaturePad.off(); // Disable drawing
                setIsSigned(true);
            }
        }
    };

    const handleClearSignature = () => {
        const signaturePad = signaturePadInstanceRef.current;
        if (signaturePad) {
            signaturePad.clear();
            signaturePad.on(); // Re-enable drawing
            onChange(field.id, null);
            setIsSigned(false);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div
                ref={containerRef}
                className={`border rounded-md ${errorClass} flex justify-center items-center p-4 overflow-hidden ${isSigned ? 'bg-gray-100' : ''}`}
                style={{ touchAction: "none" }}
            >
                <canvas ref={signatureCanvasRef} className="bg-white" />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
                {isSigned ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClearSignature}
                        className="w-full"
                    >
                        Sign Again
                    </Button>
                ) : (
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClearSignature}
                            className="flex-grow sm:flex-grow-0"
                        >
                            Clear
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveSignature}
                            className="flex-grow sm:flex-grow-0"
                        >
                            Save Signature
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};